import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Plus, Download, Eye, Edit, Sparkles } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { usePagination, useFilters, useSearch, useCrud, useApiQuery } from '../hooks/useApi';
import { licenseService, identityService } from '../services/api';
import LicenseTable from '../components/Licenses/LicenseTable';
import LicenseFilters from '../components/Licenses/LicenseFilters';
import LicenseDetailModal from '../components/Licenses/LicenseDetailModal';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import Can from '../components/Common/Can';
import { getAppHomePath } from '../constants/navigation';
import { P } from '../constants/permissions';
import { downloadJson } from '../utils/exportHelpers';
import { fetchAllPages } from '../utils/apiPagination';
import { idKey, lookupLabel, unwrapList } from '../utils/entityIds';
import { filterLicenseRows } from '../utils/licenseListFilters';
import LicencasForm from './License/Licencas';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../i18n/userFacingError';
import { useAuth } from '../contexts/AuthContext';
import { isRequesterRole } from '../utils/tenantProfile';

const API_PAGE = 1000;

/** Busca tipos, clientes, empreendimentos, pessoas e empresas com paginação (le=1000) e monta mapas de nomes. */
async function fetchLicensesLookups() {
  async function paged(getter) {
    let skip = 0;
    const acc = [];
    while (true) {
      const r = await getter({ skip, limit: API_PAGE });
      const batch = unwrapList(r.data);
      acc.push(...batch);
      if (batch.length < API_PAGE) break;
      skip += API_PAGE;
    }
    return acc;
  }

  async function pagedTipos() {
    let skip = 0;
    const acc = [];
    while (true) {
      const batch = await licenseService.tipoLicencaCrud.getList({ skip, limit: API_PAGE });
      const arr = Array.isArray(batch) ? batch : unwrapList(batch);
      acc.push(...arr);
      if (arr.length < API_PAGE) break;
      skip += API_PAGE;
    }
    return acc;
  }

  const [tipos, clients, ents, persons, companies] = await Promise.all([
    pagedTipos(),
    paged((p) => identityService.getClients(p)),
    paged((p) => identityService.getEnterprises(p)),
    paged((p) => identityService.getPersons(p)),
    paged((p) => identityService.getCompanies(p)),
  ]);

  const personNameById = {};
  for (const p of persons) {
    if (p?.id == null) continue;
    personNameById[idKey(p.id)] = (p.name || '').trim();
  }
  const companyById = {};
  for (const c of companies) {
    if (c?.id == null) continue;
    companyById[idKey(c.id)] = c.name || '';
  }

  const tipoById = {};
  for (const t of tipos) {
    if (t?.id == null) continue;
    tipoById[idKey(t.id)] = t.nome || t.name || '—';
  }

  const clientById = {};
  for (const c of clients) {
    if (c?.id == null) continue;
    let name = '';
    if (c.person_id) name = personNameById[idKey(c.person_id)] || '';
    else if (c.company_id) name = companyById[idKey(c.company_id)] || '';
    if (!name) name = c.name || '';
    if (!name) name = c.username || c.email || c.ctf || '';
    clientById[idKey(c.id)] = name || '—';
  }

  const entById = {};
  for (const e of ents) {
    if (e?.id == null) continue;
    entById[idKey(e.id)] = e.name || e.nome || '—';
  }

  const personById = {};
  for (const p of persons) {
    if (p?.id == null) continue;
    personById[idKey(p.id)] = personNameById[idKey(p.id)] || '—';
  }

  return { tipoById, clientById, entById, personById };
}

const EMPTY_LOOKUPS = { tipoById: {}, clientById: {}, entById: {}, personById: {} };

const STATUS_LABELS = {
  '1': 'Aguardando abertura',
  '2': 'Processo aberto',
  '3': 'Licença emitida',
  '4': 'Solicitada prorrogação',
  '5': 'Finalizado',
  '6': 'Suspensa',
  '7': 'Cassada',
};

function mapLicencaRow(lic, lookups) {
  const st = lic.status != null ? String(lic.status) : '';
  const entId = lic.empreendimento_id;
  const tipoId = lic.tipo_licenca_id;
  const reqId = lic.requerente_id;
  const enterprise =
    lic.empreendimento?.name ||
    lic.empreendimento_nome ||
    lookupLabel(lookups?.entById, entId) ||
    '—';
  const type = lookupLabel(lookups?.tipoById, tipoId) || '—';
  const responsible =
    lic.requerente?.name ||
    lic.requerente_nome ||
    lookupLabel(lookups?.clientById, reqId) ||
    lookupLabel(lookups?.personById, reqId) ||
    '—';
  return {
    id: lic.id,
    numero: lic.numero?.trim() || '—',
    enterprise,
    type,
    status: st,
    expirationDate: lic.validade || null,
    responsible,
    raw: lic,
  };
}

const Licenses = () => {
  const { t } = useTranslation('licenses');
  const { user } = useAuth();
  const requester = isRequesterRole(user);

  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('table');
  const [detailModal, setDetailModal] = useState(null);
  const licencaFormRef = useRef(null);

  const pagination = usePagination(1, 10);
  const filters = useFilters({
    status: '',
    type: '',
    enterprise: '',
    dateFrom: '',
    dateTo: '',
  });
  const search = useSearch();

  const queryClient = useQueryClient();
  const { useDelete } = useCrud('licencas', licenseService.licencaCrud);
  const { data: listData, isLoading, error, refetch } = useApiQuery('licencas-all-pages', () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const deleteMutation = useDelete();

  const { data: lookupsData } = useApiQuery('licenses-lookups-bundle', fetchLicensesLookups);
  const lookups = lookupsData ?? EMPTY_LOOKUPS;

  const allRows = useMemo(() => {
    const raw = unwrapList(listData);
    return raw.map((lic) => mapLicencaRow(lic, lookups));
  }, [listData, lookups]);

  const tipoOptions = useMemo(
    () =>
      Object.entries(lookups.tipoById || {}).map(([value, label]) => ({ value, label })),
    [lookups.tipoById]
  );

  const highlightFromUrl = (searchParams.get('highlight') || '').trim();
  const clientFromUrl = (searchParams.get('client') || '').trim();
  const openNewFromUrl = searchParams.get('new') === '1';

  const licenses = useMemo(
    () =>
      filterLicenseRows(allRows, {
        query: search.query,
        status: filters.filters.status,
        type: filters.filters.type,
        enterprise: filters.filters.enterprise,
        dateTo: filters.filters.dateTo,
        requerenteId: clientFromUrl,
      }),
    [allRows, search.query, filters.filters, clientFromUrl]
  );

  useEffect(() => {
    pagination.setTotal(licenses.length);
    // setTotal é estável; não queremos reexecutar quando pagination mudar de identidade
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenses.length]);

  const pagedLicenses = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return licenses.slice(start, start + pagination.limit);
  }, [licenses, pagination.page, pagination.limit]);

  /** Notificações / deep links: /licenses?highlight=<uuid> abre o modal e posiciona a paginação na linha. */
  useEffect(() => {
    if (isLoading) return;
    if (!highlightFromUrl) return;
    const highlightId = idKey(highlightFromUrl);
    setDetailModal({ id: highlightFromUrl, mode: 'view' });
    const idx = licenses.findIndex((r) => idKey(r.id) === highlightId);
    if (idx >= 0) {
      const newPage = Math.floor(idx / pagination.limit) + 1;
      if (newPage !== pagination.page) {
        pagination.setPage(newPage);
      }
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('highlight');
        return next;
      },
      { replace: true }
    );
  }, [
    isLoading,
    highlightFromUrl,
    licenses,
    pagination.page,
    pagination.limit,
    pagination.setPage,
    setSearchParams,
  ]);

  const handleExport = () => {
    downloadJson(
      `licencas-${new Date().toISOString().slice(0, 10)}.json`,
      licenses.map((r) => r.raw)
    );
    toast.success('Exportação iniciada');
  };

  const goToNewLicencaForm = (prefill) => {
    licencaFormRef.current?.openCreate(prefill);
  };

  useEffect(() => {
    if (!openNewFromUrl) return;
    goToNewLicencaForm({
      tipo_licenca_id: searchParams.get('tipo_licenca_id') || '',
      processo_id: searchParams.get('processo_id') || '',
      requerente_id: searchParams.get('requerente_id') || '',
      data_abertura_processo: searchParams.get('data_abertura_processo') || '',
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        next.delete('tipo_licenca_id');
        next.delete('processo_id');
        next.delete('requerente_id');
        next.delete('data_abertura_processo');
        return next;
      },
      { replace: true }
    );
  }, [openNewFromUrl, searchParams, setSearchParams]);

  const handleViewLicense = (license) => {
    const id = license.raw?.id || license.id;
    if (id) setDetailModal({ id, mode: 'view' });
  };

  const handleEditLicense = (license) => {
    const id = license.raw?.id || license.id;
    if (id) setDetailModal({ id, mode: 'edit' });
  };

  const handleNavigateFullEdit = (licencaId) => {
    setDetailModal(null);
    licencaFormRef.current?.openEditById(licencaId);
  };

  const handleDeleteLicense = (license) => {
    if (!window.confirm('Excluir esta licença?')) return;
    deleteMutation.mutate(license.raw?.id || license.id, {
      onSuccess: () => {
        toast.success('Licença excluída');
        queryClient.invalidateQueries('licencas-all-pages');
        queryClient.invalidateQueries({ queryKey: ['licencas'] });
        refetch();
      },
      onError: (e) => toast.error(userFacingError(e, 'deleteError')),
    });
  };

  const handleDuplicateLicense = () => {
    toast(
      'Duplicação não é automática aqui. Crie um novo registro com os mesmos vínculos no formulário, se permitido.',
      { duration: 6000 }
    );
    goToNewLicencaForm();
  };

  const getStatusBadge = (status) => {
    const key = String(status);
    const label = STATUS_LABELS[key] || key || '—';
    const colorBy = {
      '1': 'warning',
      '2': 'primary',
      '3': 'success',
      '4': 'warning',
      '5': 'secondary',
    };
    const color = colorBy[key] || 'primary';
    return <span className={`badge badge-${color}`}>{label}</span>;
  };

  if (error) {
    return (
      <FormPageShell maxWidth="w-full max-w-none" className="!p-0">
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-danger-800">
          {t('common:loadError')}{' '}
          <button type="button" className="btn btn-outline btn-sm ml-2" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell maxWidth="w-full max-w-none" className="!p-0">
      <div className="space-y-6">
        <PageHeader
          title={t("title")}
          description="Lista, filtros e cadastro completo (modal) no mesmo lugar. Tipos de licença: menu Licenciamento."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Licenças' }]}
        >
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn btn-outline shadow-sm" onClick={handleExport} disabled={!licenses.length}>
              <Download className="mr-2 h-4 w-4" aria-hidden />
              Exportar
            </button>
            {requester ? null : (
              <>
            <Can permission={P.LICENSE_ASSISTIDA}>
              <Link
                to="/licenses/assisted"
                className="btn btn-outline inline-flex items-center gap-2 border-primary-300 text-primary-800 shadow-sm"
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                Nova licença (IA)
              </Link>
            </Can>
            <Can permission={P.LICENSE_LICENCAS}>
              <button
                type="button"
                onClick={goToNewLicencaForm}
                className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Nova licença
              </button>
            </Can>
              </>
            )}
          </div>
        </PageHeader>

        <div className="data-list-card overflow-visible">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <LicenseFilters filters={filters} search={search} tipoOptions={tipoOptions} />
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            {isLoading && (
              <p className="text-sm text-gray-600" role="status">
                Carregando licenças…
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Mostrando {licenses.length} licença(s) {allRows.length !== licenses.length ? '(filtradas)' : ''}
              </div>
            </div>

            {viewMode === 'table' ? (
              <LicenseTable
                licenses={pagedLicenses}
                totalCount={licenses.length}
                pagination={pagination}
                onView={handleViewLicense}
                onEdit={requester ? undefined : handleEditLicense}
                onDelete={requester ? undefined : handleDeleteLicense}
                onDuplicate={requester ? undefined : handleDuplicateLicense}
                getStatusBadge={getStatusBadge}
                onCreate={requester ? undefined : goToNewLicencaForm}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {pagedLicenses.map((license) => (
                  <div key={license.id} className="card">
                    <div className="card-content">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {license.numero !== '—' ? license.numero : license.id?.slice(0, 12) + '…'}
                          </h3>
                          <p className="text-xs font-mono text-gray-500">{license.id}</p>
                          <p className="text-sm text-gray-600">{license.enterprise}</p>
                        </div>
                        {getStatusBadge(license.status)}
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Tipo:</span>
                          <span className="text-gray-900">{license.type}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Vencimento:</span>
                          <span className="text-gray-900">
                            {license.expirationDate
                              ? new Date(license.expirationDate).toLocaleDateString('pt-BR')
                              : '-'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Requerente:</span>
                          <span className="text-gray-900">{license.responsible}</span>
                        </div>
                      </div>

                      <Can permission={P.LICENSES_PAGE}>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewLicense(license)}
                            className="btn btn-outline btn-sm flex-1"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditLicense(license)}
                            className="btn btn-outline btn-sm flex-1"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </button>
                        </div>
                      </Can>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {detailModal?.id ? (
        <LicenseDetailModal
          licencaId={detailModal.id}
          mode={detailModal.mode}
          onClose={() => setDetailModal(null)}
          onNavigateFullEdit={handleNavigateFullEdit}
        />
      ) : null}

      <LicencasForm ref={licencaFormRef} onSaved={() => refetch()} />
    </FormPageShell>
  );
};

export default Licenses;
